import {
  createElement,
  useEffect,
  useId,
  useRef,
  type PropsWithChildren,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useBackClosable } from "@teachermate/shared/back";
import { QRCodeSVG } from "qrcode.react";
import { getLoginUrl } from "../lib/auth-config";
import { navigate } from "../lib/navigation";

const modalFocusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * 공용 서비스 네비 — 앱 최상단에 **한 번만** 둔다(표준 → docs/service-nav-standard.md).
 *
 * 🚨 `<teachermate-nav>` 는 Shadow DOM 커스텀 엘리먼트인데 **`<slot>` 이 없다** —
 * 자식을 넣어도 화면에 나오지 않는다. 절대 자식을 넣지 말 것.
 * 🚨 studio 자체 헤더를 이 아래 또 두면 브랜드가 두 번 나온다(2026-07-29 실브라우저 QA 적발).
 *
 * @param hidden 학생 QR 경로에서 숨김(표준 `hidden` 속성).
 */
export function ServiceNav({ hidden = false }: { hidden?: boolean }) {
  return (
    <div className="service-nav-wrap">
      {createElement("teachermate-nav", hidden ? { active: "AI 스튜디오", hidden: true } : { active: "AI 스튜디오" })}
    </div>
  );
}

/**
 * 학생 참여 QR 전체화면 — 라이브 현황과 시연작 바가 **같은 구현**을 쓴다.
 *
 * 🚨 조건부 숨김 금지(§9.H-14): 코드는 세션이 만들어진 뒤에만 이 화면에 닿으므로 항상 존재한다.
 * 미준비 상태는 버튼을 숨기지 말고 disabled 로 그린다.
 */
export function TeacherQrOverlay({
  title,
  code,
  participantCount,
  onClose,
}: {
  title: string;
  code: string;
  /** 생략하면 참여 인원 줄을 그리지 않는다(시연 바처럼 현황을 폴링하지 않는 곳). */
  participantCount?: number;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const descriptionId = useId();
  const joinUrl = `${window.location.origin}/join?code=${encodeURIComponent(code)}`;
  onCloseRef.current = onClose;
  // 창이 열린 채 뒤로가기 = 창만 닫힘(DESIGN-POLICY §9.H-18 v2.4 표 첫 줄)
  useBackClosable(true, () => onCloseRef.current());

  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const appRoot = document.getElementById("root");
    const appRootWasInert = appRoot?.hasAttribute("inert") ?? false;
    appRoot?.setAttribute("inert", "");
    closeRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
      } else if (event.key === "Tab") {
        event.preventDefault();
        closeRef.current?.focus({ preventScroll: true });
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (!appRootWasInert) appRoot?.removeAttribute("inert");
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <section
      className="teacher-qr-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button ref={closeRef} className="teacher-qr-close" type="button" onClick={onClose}>
        <span aria-hidden="true">×</span> 닫기
      </button>
      <div className="teacher-qr-content">
        <p className="teacher-qr-session-title">{title}</p>
        <h2 className="sr-only" id={titleId}>학생 참여 QR 코드</h2>
        <strong className="teacher-qr-code-text">{code}</strong>
        {/* 🔑 크기는 여기서 정하지 않는다 — styles.css `.teacher-qr-code svg` 한 곳뿐(DESIGN-POLICY §10 v3).
            size prop 을 되살리면 «크기가 두 곳» 이 되어, 한쪽만 고쳤을 때 화면이 안 변한다. */}
        <div className="teacher-qr-code" aria-label={`${title} 참여 QR 코드`}>
          <QRCodeSVG value={joinUrl} level="M" title={`${title} 참여 QR 코드`} />
        </div>
        {participantCount === undefined
          ? null
          : <p className="teacher-qr-participants"><span aria-hidden="true" />학생 {participantCount}명 참여 중</p>}
        <p className="teacher-qr-guidance" id={descriptionId}>QR 코드를 스캔하거나 코드를 입력하세요</p>
        <p className="teacher-qr-url">{joinUrl}</p>
      </div>
    </section>,
    document.body,
  );
}

/** 교사 하위 화면에서 대시보드로 돌아가는 링크. 공용 네비는 서비스 간 이동만 담당한다. */
export function TeacherBackLink() {
  return (
    <button type="button" className="teacher-back-link" onClick={() => navigate("/teacher")}>
      ← 내 수업
    </button>
  );
}

export function TeacherMessagePage({
  kind,
}: {
  kind: "login" | "forbidden";
}) {
  return (
    <main className="teacher-message-page">
      <section>
        <span aria-hidden="true">{kind === "login" ? "🔐" : "⛔"}</span>
        <h1>{kind === "login" ? "teachermate 로그인이 필요해요" : "내 수업이 아니에요"}</h1>
        <p>{kind === "login" ? "로그인하면 보고 있던 화면으로 다시 돌아와요." : "이 수업을 만든 교사 계정으로 열어 주세요."}</p>
        {kind === "login" ? (
          <a className="button-primary teacher-login-link" href={getLoginUrl()}>
            teachermate 로그인
          </a>
        ) : (
          <button className="button-primary" type="button" onClick={() => navigate("/teacher")}>내 수업으로</button>
        )}
      </section>
    </main>
  );
}

export function TeacherModal({
  title,
  children,
  actions,
  onClose,
  destructive = false,
  wide = false,
  returnFocusRef,
  backClosable = true,
}: PropsWithChildren<{
  title: string;
  actions?: ReactNode;
  onClose: () => void;
  destructive?: boolean;
  /** 폼을 담는 모달(수업 만들기)은 넓게 — 기본 400px 에선 폼 그리드가 넘쳐 가로 스크롤바가 생긴다. */
  wide?: boolean;
  returnFocusRef?: RefObject<HTMLElement | null>;
  /**
   * 뒤로가기로 이 창만 닫기(기본 켬). 창 안에서 다른 화면으로 가는 창은 부모가 useBackClosable 을 직접 쥐고
   * closeThen 으로 이동한다 — 그때 false 로 넘긴다(칸이 두 번 쌓이지 않게).
   */
  backClosable?: boolean;
}>) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  onCloseRef.current = onClose;
  // 창이 열린 채 뒤로가기 = 창만 닫힘(DESIGN-POLICY §9.H-18 v2.4 표 첫 줄)
  useBackClosable(backClosable, () => onCloseRef.current());

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    const trigger = returnFocusRef?.current
      ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const appRoot = document.getElementById("root");
    const appRootWasInert = appRoot?.hasAttribute("inert") ?? false;
    appRoot?.setAttribute("inert", "");

    const focusableElements = (): HTMLElement[] => Array.from(
      dialog.querySelectorAll<HTMLElement>(modalFocusableSelector),
    ).filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");

    const initialFocus = destructive
      ? dialog.querySelector<HTMLElement>("[data-teacher-modal-cancel]")
      : focusableElements()[0];
    initialFocus?.focus({ preventScroll: true });
    if (!initialFocus) dialog.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = focusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (!appRootWasInert) appRoot?.removeAttribute("inert");
      if (
        trigger?.isConnected
        && !trigger.matches(":disabled")
        && trigger.getAttribute("aria-disabled") !== "true"
        && !trigger.closest("[inert]")
      ) {
        trigger.focus({ preventScroll: true });
      }
    };
  }, [destructive, returnFocusRef]);

  return createPortal(
    <div className="teacher-modal-backdrop" onClick={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section
        ref={dialogRef}
        className={wide ? "teacher-modal teacher-modal-wide" : "teacher-modal"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <button type="button" className="button-ghost teacher-modal-close" onClick={onClose}>× 닫기</button>
        <h2 id={titleId}>{title}</h2>
        <div className="teacher-modal-body">{children}</div>
        {actions ? <div className="teacher-modal-actions">{actions}</div> : null}
      </section>
    </div>,
    document.body,
  );
}
